// src/services/aiService.js - 增强版本，支持智能规划
class AIService {
  constructor() {
    this.apiEndpoint = "https://api.openai.com/v1/chat/completions";
    this.apiKey = "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";
    this.model = "gpt-4o";

    // 🆕 智能规划相关配置
    this.smartPlanningEnabled = true;
    this.conversationMemory = new Map(); // 对话记忆
    this.userPreferenceCache = new Map(); // 用户偏好缓存

    console.log("AI Service initialized with smart planning support:", {
      endpoint: this.apiEndpoint,
      hasKey: !!this.apiKey,
      model: this.model,
      smartPlanningEnabled: this.smartPlanningEnabled
    });
  }


  async sendMessage(message, context = {}) {
    try {
      const systemPrompt = this.buildEnhancedSystemPrompt(context);
      const conversationHistory = this.buildConversationHistory(context);

      const requestBody = {
        model: this.model,
        messages: [
          {
            role: "system",
            content: systemPrompt,
          },
          ...conversationHistory, // 🆕 添加对话历史
          {
            role: "user",
            content: message,
          },
        ],
        temperature: 0.7,
        max_tokens: 2000, // 🔧 增加token限制以支持更详细的规划
        top_p: 0.9,
        frequency_penalty: 0.1,
        presence_penalty: 0.1,
      };

      console.log("🚀 Sending request with context:", {
        model: this.model,
        messageLength: message.length,
        context: context.currentPage || context.currentStage,
        hasItinerary: context.hasItinerary,
        smartPlanActive: context.smartPlanActive
      });

      const response = await fetch(this.apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `HTTP ${response.status}: ${errorData.error?.message || "API Error"}`
        );
      }

      const data = await response.json();
      const aiMessage = data.choices[0]?.message?.content;

      if (!aiMessage) {
        throw new Error("Empty response from AI");
      }

      console.log(
        "✅ AI Response received:",
        aiMessage.substring(0, 100) + "..."
      );

      // 🆕 更新对话记忆
      this.updateConversationMemory(context.sessionId, message, aiMessage);

      // 🔧 根据上下文智能处理响应
      return this.processEnhancedAIResponse(aiMessage, context);
    } catch (error) {
      console.error("❌ AI Service Error:", error);
      throw error;
    }
  }
  // Detect user intent + entity (e.g., restaurant name) from free-form text
  async extractIntentEntity(userInput) {
    const prompt = `
  You are a travel assistant. Read the user's message and return the user's intent and entity in strict JSON format.
  
  Only respond with this JSON:
  {
    "intent": "string",  // one of: change_restaurant, add_activity, remove_activity, change_hotel, unknown
    "entity": "string"   // the name of the restaurant, activity, hotel, etc.
  }
  
  User message: "${userInput}"
  `;
  
    const requestBody = {
      model: this.model,
      messages: [
        { role: "system", content: "You extract structured intent from user travel-related messages." },
        { role: "user", content: prompt },
      ],
      temperature: 0,
      max_tokens: 100,
    };
  
    try {
      const response = await fetch(this.apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify(requestBody),
      });
  
      const data = await response.json();
      let content = data.choices?.[0]?.message?.content?.trim();
  
      // Remove code fences like ```json ... ```
      if (content.startsWith("```")) {
        content = content.replace(/```(?:json)?\n?/, "").replace(/```$/, "").trim();
      }
  
      const parsed = JSON.parse(content);
      console.log("Extracted intent/entity:", parsed);
      return parsed;
    } catch (error) {
      console.error("Intent parsing failed:", error);
      return { intent: "unknown", entity: null };
    }
  }
  


  // 🆕 构建增强的系统提示
  buildEnhancedSystemPrompt(context) {
    const { 
      currentStage, 
      userChoices, 
      hasItinerary, 
      currentPage, 
      smartPlanActive,
      existingItinerary,
      preferences 
    } = context;

    let basePrompt = `You are Concord, the specialized Montreal AI travel assistant with advanced natural language processing capabilities.

🍁 CORE SPECIALIZATION: You EXCLUSIVELY plan trips to Montreal, Quebec, Canada.

🧠 ENHANCED CAPABILITIES:
- Advanced date/time parsing and understanding
- Intelligent itinerary generation and modification
- Context-aware conversation management
- Multi-language support (English/French/Chinese)
- Smart preference learning and adaptation

📅 SMART PLANNING MODE: ${smartPlanActive ? 'ACTIVE' : 'INACTIVE'}`;

    if (smartPlanActive && hasItinerary) {
      basePrompt += `\n\n🎯 CURRENT CONTEXT: User has an active ${existingItinerary?.length || 'multi'}-day Montreal itinerary.
MODE: Itinerary Management & Optimization
CAPABILITIES: Modify specific days, swap activities, adjust timing, answer detailed questions about locations.`;
    } else if (currentPage === 'mainPage') {
      basePrompt += `\n\n🏠 MAIN PAGE MODE: Initial engagement and trip inspiration.
FOCUS: Detect travel planning intent, extract dates/duration, provide Montreal-specific inspiration.`;
    } else {
      basePrompt += `\n\n💬 CHAT MODE: Interactive planning and assistance.
FOCUS: Preference collection, detailed planning, Q&A about Montreal attractions.`;
    }

    // 添加用户偏好和历史上下文
    if (userChoices && Object.keys(userChoices).length > 0) {
      basePrompt += `\n\n👤 USER PREFERENCES:
${Object.entries(userChoices).map(([key, value]) => `- ${key}: ${value}`).join('\n')}`;
    }

    if (preferences && Object.keys(preferences).length > 0) {
      basePrompt += `\n\n🎨 EXTRACTED PREFERENCES:
${Object.entries(preferences).map(([key, value]) => `- ${key}: ${value}`).join('\n')}`;
    }

    basePrompt += `\n\n📍 MONTREAL EXPERTISE AREAS:
- Historic Old Montreal (Vieux-Montréal) & cobblestone streets
- Mount Royal Park & scenic lookouts
- Cultural venues: MMFA, Notre-Dame Basilica, Olympic Stadium
- Food scene: Poutine, bagels, smoked meat, fine dining
- Neighborhoods: Plateau, Mile End, Little Italy, Chinatown
- Markets: Jean-Talon, Atwater, Bonsecours
- Seasonal activities & festivals
- Transportation: Metro, BIXI bikes, walking routes

🎯 RESPONSE GUIDELINES:
- Always maintain Montreal focus
- Use natural, conversational tone with appropriate emojis (1-2 per response)
- Provide specific, actionable recommendations
- Include practical details (timing, costs, locations)
- Ask follow-up questions to refine suggestions
- Handle date/time expressions intelligently
- Support itinerary modifications with precision

🚫 STRICT BOUNDARIES:
- Never recommend destinations outside Montreal/Quebec region
- Redirect other city inquiries back to Montreal
- If asked about other destinations: "I specialize exclusively in Montreal! Here's why Montreal is perfect for your trip..."

RESPONSE FORMAT: Provide helpful, detailed responses in natural language. If generating structured data, use clear formatting.`;

    return basePrompt;
  }

  // 🆕 构建对话历史
  buildConversationHistory(context) {
    const sessionId = context.sessionId || 'default';
    const history = this.conversationMemory.get(sessionId) || [];
    
    // 只保留最近5轮对话以控制token使用
    return history.slice(-10).map(entry => [
      { role: 'user', content: entry.userMessage },
      { role: 'assistant', content: entry.aiResponse }
    ]).flat();
  }

  // 🆕 更新对话记忆
  updateConversationMemory(sessionId = 'default', userMessage, aiResponse) {
    if (!this.conversationMemory.has(sessionId)) {
      this.conversationMemory.set(sessionId, []);
    }
    
    const history = this.conversationMemory.get(sessionId);
    history.push({
      userMessage,
      aiResponse,
      timestamp: new Date().toISOString()
    });
    
    // 保持最近20轮对话
    if (history.length > 20) {
      history.splice(0, history.length - 20);
    }
  }

  // 🆕 增强的AI响应处理
  processEnhancedAIResponse(aiMessage, context) {
    // 检查蒙特利尔边界合规性
    if (!this.validateMontrealFocus(aiMessage)) {
      console.warn('🚨 AI mentioned non-Montreal content, applying correction');
      aiMessage += "\n\nBut let me focus on what makes Montreal special for your trip! 🍁";
    }

    // 尝试解析结构化响应
    try {
      const parsed = JSON.parse(aiMessage);
      return this.enhanceStructuredResponse(parsed, context);
    } catch (e) {
      // 非JSON响应 - 进行智能增强
      return this.enhanceNaturalResponse(aiMessage, context);
    }
  }
  

  // 🆕 验证蒙特利尔专注度
  validateMontrealFocus(message) {
    const forbiddenCities = [
      'toronto', 'new york', 'las vegas', 'vancouver', 'ottawa', 
      'calgary', 'winnipeg', 'halifax', 'boston', 'paris'
    ];
    
    const lowerMessage = message.toLowerCase();
    
    // 检查是否提到禁止的城市（但允许在重定向语境中）
    const mentionsForbiddenCity = forbiddenCities.some(city => 
      lowerMessage.includes(city) && 
      !lowerMessage.includes('specialize') &&
      !lowerMessage.includes('focus on montreal') &&
      !lowerMessage.includes('montreal is better')
    );
    
    return !mentionsForbiddenCity;
  }

  // 🆕 增强结构化响应
  enhanceStructuredResponse(parsed, context) {
    if (parsed.type === 'preference' && !parsed.options) {
      const defaultOptions = {
        activity: ["Adventure", "Nature", "City Strolls", "Cultural Immersion"],
        cuisine: ["French", "Italian", "Local Specialties", "Asian Fusion"],
        tier: ["Basic", "Signature", "Luxury"],
        modify_day: ["更换景点", "调整时间", "更改餐厅", "重新规划整天"]
      };
      parsed.options = defaultOptions[parsed.stage] || [];
    }
    
    return parsed;
  }

  // 🆕 增强自然语言响应
  enhanceNaturalResponse(aiMessage, context) {
    const response = {
      type: "message",
      message: aiMessage.trim()
    };

    // 为主页生成增强功能
    if (context.currentPage === 'mainPage') {
      Object.assign(response, this.generateMainPageEnhancements(aiMessage, context));
    }
    
    // 为智能规划模式生成特殊建议
    if (context.smartPlanActive) {
      Object.assign(response, this.generateSmartPlanEnhancements(aiMessage, context));
    }

    // 检测用户意图并提供相应建议
    const detectedIntent = this.detectUserIntent(aiMessage, context);
    if (detectedIntent) {
      response.suggestedActions = detectedIntent.actions;
      response.confidence = detectedIntent.confidence;
    }

    return response;
  }

  // 🆕 生成智能规划增强功能
  generateSmartPlanEnhancements(aiMessage, context) {
    const enhancements = {};
    const lowerMessage = aiMessage.toLowerCase();

    // 如果AI提到了修改或调整
    if (lowerMessage.includes('修改') || lowerMessage.includes('调整') || 
        lowerMessage.includes('modify') || lowerMessage.includes('change')) {
      enhancements.quickActions = [
        { label: '修改第1天', action: 'modify_day_1' },
        { label: '修改第2天', action: 'modify_day_2' },
        { label: '调整时间', action: 'adjust_timing' },
        { label: '更换餐厅', action: 'change_restaurants' }
      ];
    }

    // 如果AI提到了具体地点
    const montrealAttractions = ['old montreal', 'mount royal', 'notre-dame', 'jean-talon', 'plateau'];
    const mentionsAttraction = montrealAttractions.some(attraction => 
      lowerMessage.includes(attraction)
    );
    
    if (mentionsAttraction) {
      enhancements.relatedSuggestions = [
        '了解更多关于这个地点',
        '查看附近的餐厅',
        '获取交通信息',
        '添加到我的收藏'
      ];
    }

    return enhancements;
  }

  // 🆕 用户意图检测
  detectUserIntent(aiMessage, context) {
    const intents = {
      planning: {
        keywords: ['plan', 'itinerary', '计划', '行程', 'schedule'],
        actions: ['开始规划行程', '查看示例行程', '设置偏好']
      },
      modification: {
        keywords: ['change', 'modify', '修改', '调整', 'adjust'],
        actions: ['修改行程', '更换活动', '调整时间']
      },
      information: {
        keywords: ['tell me', 'what is', '什么是', '介绍', 'about'],
        actions: ['查看详细信息', '获取更多建议', '探索相关内容']
      },
      booking: {
        keywords: ['book', 'reserve', '预订', '预约', '购买'],
        actions: ['查看预订选项', '比较价格', '查看可用性']
      }
    };

    const lowerMessage = aiMessage.toLowerCase();
    let bestMatch = null;
    let maxScore = 0;

    for (const [intent, config] of Object.entries(intents)) {
      const score = config.keywords.reduce((sum, keyword) => 
        sum + (lowerMessage.includes(keyword) ? 1 : 0), 0
      );
      
      if (score > maxScore) {
        maxScore = score;
        bestMatch = { intent, actions: config.actions, confidence: score / config.keywords.length };
      }
    }

    return maxScore > 0 ? bestMatch : null;
  }

  // 保持原有的主页增强功能
  generateMainPageEnhancements(aiMessage, context) {
    const lowerMessage = aiMessage.toLowerCase();
    const suggestions = [];
    const actionButtons = [];

    // 根据AI回复内容生成智能建议
    if (lowerMessage.includes("montreal") || lowerMessage.includes("蒙特利尔")) {
      suggestions.push(
        "规划5天蒙特利尔深度游",
        "蒙特利尔美食探索之旅", 
        "蒙特利尔文化艺术体验",
        "蒙特利尔预算友好选择"
      );
    } else if (lowerMessage.includes("budget") || lowerMessage.includes("预算")) {
      suggestions.push(
        "经济型蒙特利尔3日游",
        "中档蒙特利尔5日游",
        "豪华蒙特利尔一周游",
        "学生党蒙特利尔周末游"
      );
    } else {
      suggestions.push(
        "开始蒙特利尔行程规划",
        "周末蒙特利尔短途游",
        "蒙特利尔必去景点推荐",
        "蒙特利尔当地美食指南"
      );
    }

    // 根据内容添加操作按钮
    if (lowerMessage.includes("plan") || lowerMessage.includes("trip") || 
        lowerMessage.includes("计划") || lowerMessage.includes("行程")) {
      actionButtons.push({
        type: "navigate",
        path: "/chat",
        label: "🚀 开始详细规划",
        variant: "primary",
      });
    }

    if (lowerMessage.includes("budget") || lowerMessage.includes("预算")) {
      actionButtons.push({
        type: "navigate", 
        path: "/budget-tiers",
        label: "💰 查看预算选项",
        variant: "outline-success",
      });
    }

    return {
      suggestions: suggestions.slice(0, 4),
      actionButtons: actionButtons.slice(0, 2)
    };
  }

  // 🆕 清理对话记忆
  clearConversationMemory(sessionId = 'default') {
    this.conversationMemory.delete(sessionId);
  }

  // 🆕 设置用户偏好缓存
  setUserPreferences(userId, preferences) {
    this.userPreferenceCache.set(userId, {
      ...preferences,
      updatedAt: new Date().toISOString()
    });
  }

  // 🆕 获取用户偏好
  getUserPreferences(userId) {
    return this.userPreferenceCache.get(userId) || {};
  }

  // 🧪 测试连接 - 增强版
  async testConnection() {
    try {
      const response = await this.sendMessage(
        "Hello, this is a test message for smart planning capabilities.",
        {
          currentPage: "test",
          smartPlanningEnabled: true
        }
      );
      return { success: true, response, smartPlanningSupported: true };
    } catch (error) {
      return { success: false, error: error.message, smartPlanningSupported: false };
    }
  }

  // 🆕 获取服务状态
  getServiceStatus() {
    return {
      apiEndpoint: this.apiEndpoint,
      model: this.model,
      smartPlanningEnabled: this.smartPlanningEnabled,
      conversationMemorySize: this.conversationMemory.size,
      userPreferenceCacheSize: this.userPreferenceCache.size,
      status: 'ready'
    };
  }
}

export default new AIService();